import { Controller } from "@hotwired/stimulus";

// Handles browser notification permission requests for the onboarding checklist.
// Updates the user's browser_notifications_enabled preference on the server.
export default class extends Controller {
  static targets = ["toggle", "status", "statusText"];
  static values = { serverEnabled: { type: Boolean, default: false } };

  connect() {
    this.updateUIFromBrowserState();
  }

  // Whether notifications are effectively enabled (both browser permission
  // granted and server-side preference enabled).
  isEffectivelyEnabled() {
    if (!("Notification" in window)) {
      return false;
    }
    return Notification.permission === "granted" && this.serverEnabledValue;
  }

  updateUIFromBrowserState() {
    if (!("Notification" in window)) {
      return;
    }

    const isEnabled = this.isEffectivelyEnabled();

    if (this.hasToggleTarget) {
      this.toggleTarget.checked = isEnabled;
    }

    if (this.hasStatusTarget) {
      this.statusTarget.classList.toggle("bg-green-500", isEnabled);
      this.statusTarget.classList.toggle("bg-gray-300", !isEnabled);
      this.statusTarget.classList.toggle("dark:bg-gray-600", !isEnabled);
    }

    if (this.hasStatusTextTarget) {
      const enabledText =
        this.statusTextTarget.dataset.enabledText || "Enabled";
      const disabledText =
        this.statusTextTarget.dataset.disabledText || "Disabled";
      this.statusTextTarget.textContent = isEnabled ? enabledText : disabledText;
    }
  }

  async request() {
    if (!("Notification" in window)) {
      this.showToast("Your browser does not support notifications.", "warning");
      return;
    }

    try {
      const permission = await Notification.requestPermission();

      if (permission === "granted") {
        await this.updateServerPreference(true);
        this.serverEnabledValue = true;
        this.showToast("Notifications enabled! You'll receive call alerts.", "success");
        this.updateUIFromBrowserState();
        this.reloadAfterToast();
      } else if (permission === "denied") {
        this.showToast(
          "Notifications are blocked by your browser. Please enable them in your browser settings and try again.",
          "error"
        );
        this.updateUIFromBrowserState();
      } else {
        // "default" — user dismissed without choosing
        this.showToast("Notification permission was dismissed.", "info");
        this.updateUIFromBrowserState();
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      this.showToast("Something went wrong. Please try again.", "error");
    }
  }

  async toggle(event) {
    if (!("Notification" in window)) {
      console.warn("Browser does not support notifications");
      event.preventDefault();
      return;
    }

    const wantsEnabled = event.target.checked;

    if (wantsEnabled) {
      // User wants to enable - need to request permission
      if (Notification.permission === "granted") {
        // Already granted, just update server
        await this.updateServerPreference(true);
        this.serverEnabledValue = true;
        this.showToast("Notifications enabled.", "success");
        this.updateUIFromBrowserState();
        this.reloadAfterToast();
      } else if (Notification.permission === "denied") {
        // Permission was denied - can't re-request, show instruction
        event.target.checked = false;
        this.showPermissionBlockedMessage();
      } else {
        // Permission is "default" — request via browser dialog
        try {
          const permission = await Notification.requestPermission();
          if (permission === "granted") {
            await this.updateServerPreference(true);
            this.serverEnabledValue = true;
            this.showToast("Notifications enabled! You'll receive call alerts.", "success");
            this.updateUIFromBrowserState();
            this.reloadAfterToast();
          } else {
            event.target.checked = false;
            if (permission === "denied") {
              this.showToast(
                "Notifications are blocked by your browser. Please enable them in your browser settings and try again.",
                "error"
              );
            }
          }
        } catch (error) {
          console.error("Error requesting notification permission:", error);
          event.target.checked = false;
          this.showToast("Something went wrong. Please try again.", "error");
        }
      }
    } else {
      // User wants to disable - update server preference and local state
      await this.updateServerPreference(false);
      this.serverEnabledValue = false;
      this.showToast("Notifications disabled.", "info");
      this.updateUIFromBrowserState();
      this.reloadAfterToast();
    }
  }

  // Reloads the page after a short delay so the toast is visible.
  reloadAfterToast() {
    setTimeout(() => {
      window.location.reload();
    }, 800);
  }

  showPermissionBlockedMessage() {
    const message =
      "Notifications are blocked by your browser. Please enable them in your browser settings and try again.";
    alert(message);
    this.showToast(message, "error");
  }

  // Shows a toast notification using the app's flash container pattern.
  showToast(message, variant = "info") {
    const flashContainer = document.getElementById("flash");
    if (!flashContainer) return;

    const toast = document.createElement("div");
    toast.className = `app-toast app-toast--${variant}`;
    toast.setAttribute(
      "role",
      variant === "error" ? "alert" : "status"
    );
    toast.setAttribute(
      "aria-live",
      variant === "error" ? "assertive" : "polite"
    );
    toast.setAttribute("aria-atomic", "true");

    const iconPaths = {
      success:
        "<path d=\"m5 12.5 4.25 4.25L19 7\" />",
      error:
        "<path d=\"M12 8v5m0 3.5v.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z\" />",
      warning:
        "<path d=\"M12 9v4m0 3.5v.01M10.3 4.4 2.8 17.2A1.2 1.2 0 0 0 3.85 19h16.3a1.2 1.2 0 0 0 1.05-1.8L13.7 4.4a1.97 1.97 0 0 0-3.4 0Z\" />",
      info:
        "<path d=\"M12 11v6m0-9v.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z\" />",
    };
    const iconPath = iconPaths[variant] || iconPaths.info;

    toast.innerHTML = `
      <div class="app-toast__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">${iconPath}</svg>
      </div>
      <div class="app-toast__content">
        <div class="app-toast__message">${message}</div>
      </div>
    `;

    flashContainer.appendChild(toast);

    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      toast.classList.add("dismissible--closing");
      setTimeout(() => toast.remove(), 300);
    }, 5000);
  }

  async updateServerPreference(enabled) {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content;

    // Build form data to match the settings controller expectations
    const formData = new FormData();
    formData.append("notifications[browser_enabled]", enabled ? "1" : "0");

    try {
      await fetch(this.settingsUrl, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "text/html",
        },
        body: formData,
      });
    } catch (error) {
      console.error("Error updating notification preference:", error);
    }
  }

  get settingsUrl() {
    // Get the organization identifier from the current URL
    const pathMatch = window.location.pathname.match(/^\/([^/]+)\/([^/]+)/);
    if (pathMatch) {
      return `/${pathMatch[1]}/${pathMatch[2]}/settings/notifications`;
    }
    return "/settings/notifications";
  }
}
